// Plynulý seznam s vykreslováním jen viditelných položek (koncepce A, postup 62): seznam zná celkový počet,
// nenačtené řádky jsou zástupci (šedé pruhy), vykreslí se jen okno kolem posunu + rezerva a zbytek nahradí odsazení.
// Společné pro tabulku (IwTable) i dlaždice / dvouřádkový seznam (IwCards); data dočítá pohled (ListView) přes onRange.
import type { Row } from './table';

export const PH = '__ph';

/** Zástupce ještě nenačteného řádku na pozici i. */
export function placeholder(i: number, rowId: string): Row {
  return { [rowId]: '__ph:' + i, [PH]: true };
}

export function isPh(r: Row | undefined): boolean {
  return r === undefined || r[PH] === true;
}

export interface VWindow {
  start: number;
  end: number;
  padTop: number;
  padBottom: number;
}

/** Rozsah vykreslených položek: perLine položek na řádek (mřížka), lineH výška řádku, rezerva overscan řádků. */
export function vwindow(total: number, perLine: number, lineH: number, scrollTop: number, viewH: number, overscan = 8): VWindow {
  const lines = Math.ceil(total / perLine);
  if (!lines) return { start: 0, end: 0, padTop: 0, padBottom: 0 };
  const first = Math.max(0, Math.floor(scrollTop / lineH) - overscan);
  const last = Math.max(first, Math.min(lines - 1, Math.ceil((scrollTop + viewH) / lineH) + overscan));
  return { start: first * perLine, end: Math.min(total, (last + 1) * perLine), padTop: first * lineH, padBottom: Math.max(0, (lines - last - 1) * lineH) };
}

/** Klávesa → nový index kurzoru (šipky, Home/End, PageUp/PageDown); perLine dlaždic na řádek. null = jiná klávesa. */
export function navTarget(key: string, cur: number, total: number, perLine: number, perPage: number): number | null {
  if (!total) return null;
  const last = total - 1;
  switch (key) {
    case 'ArrowDown':
      return cur < 0 ? 0 : Math.min(last, cur + perLine);
    case 'ArrowUp':
      return cur < 0 ? 0 : Math.max(0, cur - perLine);
    case 'ArrowRight':
      return perLine > 1 ? Math.min(last, cur + 1) : null;
    case 'ArrowLeft':
      return perLine > 1 ? Math.max(0, cur - 1) : null;
    case 'Home':
      return 0;
    case 'End':
      return last;
    case 'PageDown':
      return Math.min(last, Math.max(0, cur) + perPage);
    case 'PageUp':
      return Math.max(0, cur - perPage);
  }
  return null;
}

/**
 * Id řádků v rozsahu a..b (Shift+klik, Shift+šipka) podle pozice v seznamu – i přes nenačtené: ty si nejdřív dočte
 * (loadRange); co se dočíst nepodaří, vynechá.
 */
export async function rangeIds(rows: () => Row[], rowId: string, a: number, b: number, loadRange: ((from: number, to: number) => Promise<void>) | null): Promise<string[]> {
  const from = Math.min(a, b);
  const to = Math.max(a, b) + 1;
  if (loadRange && rows().slice(from, to).some((r) => isPh(r))) await loadRange(from, to);
  return rows().slice(from, to).filter((r) => !isPh(r)).map((r) => String(r[rowId]));
}

/**
 * Seznam s nadpisy skupin (Pošta: Dnes / Včera / Tento týden…): nadpis výšky headH stojí před položkou na pozici heads[k]
 * (vzestupně). Pozice jsou známé dopředu (ze serveru), takže se výška seznamu při dočítání nemění a nic neposkakuje.
 */
export function headOffset(i: number, lineH: number, heads: number[], headH: number): number {
  let n = 0;
  for (const p of heads) if (p <= i) n++;
  return i * lineH + n * headH;
}

/** Celková výška seznamu s nadpisy. */
export function headTotal(total: number, lineH: number, heads: number[], headH: number): number {
  return total * lineH + heads.filter((p) => p < total).length * headH;
}

/** Jako vwindow, ale s nadpisy skupin: padTop končí nad nadpisem první vykreslené položky (pokud ho má). */
export function headWindow(total: number, lineH: number, heads: number[], headH: number, scrollTop: number, viewH: number, overscan = 8): VWindow {
  if (!total) return { start: 0, end: 0, padTop: 0, padBottom: 0 };
  const at = (y: number): number => { // poslední položka, jejíž horní okraj je nad y
    let lo = 0;
    let hi = total - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (headOffset(mid, lineH, heads, headH) <= y) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  };
  const start = Math.max(0, at(scrollTop) - overscan);
  const end = Math.min(total, at(scrollTop + viewH) + 1 + overscan);
  const padTop = headOffset(start, lineH, heads, headH) - (heads.includes(start) ? headH : 0);
  const padBottom = Math.max(0, headTotal(total, lineH, heads, headH) - headOffset(end - 1, lineH, heads, headH) - lineH);
  return { start, end, padTop, padBottom };
}
